import { api } from '@/api/clients'
import { queryClient } from '@/api/queryClient'
import { routeForSignInError, toGameError, type GameError } from '@/api/errors'
import { setRefreshHandler } from '@/api/transports'
import { firebaseConfigured, isMockMode } from '@/config/runtime'
import type { AuthResponse } from '@/gen/player/v1/player_pb'
import { useMapStore } from '@/map/mapStore'
import {
  emailPasswordIdToken,
  firebaseSignOut,
  freshIdToken,
  googlePopupIdToken,
  restoredIdToken,
} from './firebase'
import { createRefresher, PROACTIVE_REFRESH_MS, type SessionEndReason } from './refresh'
import { useSession } from './session'

/** The ID token the mock WebSession accepts. Never reaches a real server. */
export const DEMO_ID_TOKEN = 'demo-firebase-id-token'
const DEMO_SESSION_KEY = 'mafiago.demo-session'

export type SignInOutcome = { ok: true } | { ok: false; error: GameError; redirect: string | null }

let proactiveTimer: ReturnType<typeof setTimeout> | null = null

function scheduleProactiveRefresh(): void {
  if (proactiveTimer) clearTimeout(proactiveTimer)
  proactiveTimer = setTimeout(() => {
    void refresher.refresh(null, { force: true })
  }, PROACTIVE_REFRESH_MS)
}

function applyAuth(res: AuthResponse, opts?: { demo?: boolean }): void {
  useSession.getState().setAuth(res, opts)
  scheduleProactiveRefresh()
}

function clearLocal(reason?: SessionEndReason): void {
  if (proactiveTimer) clearTimeout(proactiveTimer)
  proactiveTimer = null
  try {
    sessionStorage.removeItem(DEMO_SESSION_KEY)
  } catch {
    /* storage may be unavailable */
  }
  useSession.getState().clear(reason)
  queryClient.clear()
  // The next account must not resume this one's camera (it may be centred
  // on the previous player's home turf).
  useMapStore.getState().reset()
}

async function reauth(): Promise<AuthResponse | null> {
  if (useSession.getState().demo) {
    return api().webSession.signIn({ firebaseIdToken: DEMO_ID_TOKEN })
  }
  const idToken = await freshIdToken()
  if (!idToken) return null
  return api().webSession.signIn({ firebaseIdToken: idToken })
}

const refresher = createRefresher({
  getTokens: () => {
    const s = useSession.getState()
    return { accessToken: s.accessToken, refreshToken: s.refreshToken }
  },
  refreshRpc: (refreshToken) => api().webSession.refresh({ refreshToken }),
  reauth,
  apply: (res) => applyAuth(res),
  // RequireAuth sends a signed-out player to /login on its own; the reason,
  // if any, is what the login screen tells them.
  fail: (reason) => clearLocal(reason),
})

setRefreshHandler((used) => refresher.refresh(used))

async function exchange(idToken: string, opts?: { demo?: boolean }): Promise<SignInOutcome> {
  try {
    const res = await api().webSession.signIn({ firebaseIdToken: idToken })
    applyAuth(res, opts)
    return { ok: true }
  } catch (err) {
    const error = toGameError(err)
    const redirect = routeForSignInError(error)
    // A refusal that names the account (no account, setup incomplete,
    // deleted, banned) would repeat on every reload while Firebase keeps its
    // session, so drop the Firebase session too. A transport failure keeps it.
    if (error.code !== 'unavailable' && !opts?.demo) await firebaseSignOut().catch(() => undefined)
    useSession.getState().setStatus('signedOut')
    return { ok: false, error, redirect }
  }
}

async function fromFirebase(getToken: () => Promise<string>): Promise<SignInOutcome> {
  let idToken: string
  try {
    idToken = await getToken()
  } catch (err) {
    return { ok: false, error: toGameError(err), redirect: null }
  }
  return exchange(idToken)
}

export function signInWithGoogle(): Promise<SignInOutcome> {
  return fromFirebase(googlePopupIdToken)
}

export function signInWithEmail(email: string, password: string): Promise<SignInOutcome> {
  return fromFirebase(() => emailPasswordIdToken(email, password))
}

/** Demo mode only: signs in against the mock transport, bypassing Firebase. */
export async function signInDemo(): Promise<SignInOutcome> {
  const outcome = await exchange(DEMO_ID_TOKEN, { demo: true })
  if (outcome.ok) {
    try {
      sessionStorage.setItem(DEMO_SESSION_KEY, '1')
    } catch {
      /* storage may be unavailable */
    }
  }
  return outcome
}

function readDemoFlag(): boolean {
  try {
    return sessionStorage.getItem(DEMO_SESSION_KEY) === '1'
  } catch {
    return false
  }
}

let bootstrapped: Promise<SignInOutcome | null> | null = null

/**
 * Called once on app load. Firebase restores its own session; if there is
 * one, it is exchanged silently. Resolves to the outcome when an exchange
 * was attempted (so the caller can route a refusal), else null.
 */
export function bootstrapSession(): Promise<SignInOutcome | null> {
  if (!bootstrapped) bootstrapped = runBootstrap()
  return bootstrapped
}

async function runBootstrap(): Promise<SignInOutcome | null> {
  const setStatus = useSession.getState().setStatus
  if (isMockMode()) {
    if (readDemoFlag()) return signInDemo()
    setStatus('signedOut')
    return null
  }
  if (!firebaseConfigured()) {
    setStatus('signedOut')
    return null
  }
  try {
    const idToken = await restoredIdToken()
    if (!idToken) {
      setStatus('signedOut')
      return null
    }
    return exchange(idToken)
  } catch (err) {
    setStatus('signedOut')
    return { ok: false, error: toGameError(err), redirect: null }
  }
}

/**
 * WebSession.SignOut revokes exactly this refresh token — never the phone's
 * session or its background-location token (which PlayerLogout would).
 */
export async function signOut(): Promise<void> {
  const { refreshToken } = useSession.getState()
  if (refreshToken) {
    await api()
      .webSession.signOut({ refreshToken })
      .catch(() => undefined)
  }
  await firebaseSignOut().catch(() => undefined)
  clearLocal()
}
